/** Overhead icon, entity tint, or attached aura for active buffs/debuffs/passives. */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { AuraRing, AuraRingConfig } from './AuraRing';
import { ensureSoftCircleTexture, CRISP_SCALE } from '../../ui/CrispUtil';

export interface StatusIndicatorConfig extends VfxPrimitiveConfig {
  type: 'tint' | 'icon' | 'aura';
  tintColor?: number;
  iconTextureKey?: string;
  iconOffsetY: number;
  auraConfig?: AuraRingConfig;
  stackMax: number;
  stackDisplay: 'count' | 'intensity';
  showDuration: boolean;
  breatheScale?: number;
  breathePeriodMs?: number;
}

export class StatusIndicator extends VfxPrimitive<StatusIndicatorConfig> {
  private iconImg: Phaser.GameObjects.Image | null = null;
  private breatheTween: Phaser.Tweens.Tween | null = null;
  private auraDelegate: AuraRing | null = null;
  private stackText: Phaser.GameObjects.Text | null = null;
  private playing = false;
  private currentStacks = 1;

  protected allocatePool(): void {
    const { config } = this;

    if (config.type === 'icon') {
      const key = config.iconTextureKey
        ?? ensureSoftCircleTexture(this.scene, config.textureSize * CRISP_SCALE);
      this.iconImg = this.createPoolImage(key);

      if (config.stackDisplay === 'count' && config.stackMax > 1) {
        this.stackText = this.scene.add.text(0, 0, '', {
          fontSize: '10px',
          color: '#ffffff',
          fontFamily: 'monospace',
          stroke: '#000000',
          strokeThickness: 2,
        });
        this.stackText.setOrigin(0.5, 0.5).setVisible(false).setActive(false);
      }
    }

    if (config.type === 'aura' && config.auraConfig) {
      this.auraDelegate = new AuraRing(this.scene, config.auraConfig);
    }
  }

  play(x: number, y: number, depth: number): void {
    this.playing = true;
    this.currentStacks = 1;
    const d = depth + (this.config.depthOffset ?? 0);

    if (this.config.type === 'icon' && this.iconImg) {
      this.activate(this.iconImg, x, y + this.config.iconOffsetY, d + 10);
      this.startBreathe();

      if (this.stackText) {
        this.stackText.setVisible(false).setActive(false);
      }
    }

    if (this.config.type === 'aura' && this.auraDelegate) {
      this.auraDelegate.play(x, y, depth);
    }
  }

  stop(): void {
    this.playing = false;
    this.killBreatheTween();

    if (this.config.type === 'icon' && this.iconImg?.active) {
      this.scene.tweens.add({
        targets: this.iconImg,
        alpha: 0,
        duration: 200,
        onComplete: () => {
          if (this.iconImg) this.deactivate(this.iconImg);
          if (this.stackText) {
            this.stackText.setVisible(false).setActive(false);
          }
        },
      });
    }

    if (this.config.type === 'aura' && this.auraDelegate) {
      this.auraDelegate.stop();
    }
  }

  updatePosition(x: number, y: number, depth: number, _delta: number): void {
    if (!this.playing) return;
    const d = depth + (this.config.depthOffset ?? 0);

    if (this.config.type === 'icon' && this.iconImg?.active) {
      this.iconImg.setPosition(x, y + this.config.iconOffsetY).setDepth(d + 10);
      if (this.stackText?.active) {
        this.stackText.setPosition(x + 8, y + this.config.iconOffsetY).setDepth(d + 11);
      }
    }

    if (this.config.type === 'aura' && this.auraDelegate) {
      this.auraDelegate.updatePosition(x, y, depth, _delta);
    }
  }

  /** Update stack count — adjusts visual intensity or count display. */
  setStacks(count: number): void {
    const clamped = Math.min(Math.max(count, 0), this.config.stackMax);
    this.currentStacks = clamped;

    if (!this.playing) return;

    if (this.config.stackDisplay === 'count' && this.stackText) {
      if (clamped > 1) {
        this.stackText.setText(`${clamped}`).setVisible(true).setActive(true);
      } else {
        this.stackText.setVisible(false).setActive(false);
      }
    }

    if (this.config.stackDisplay === 'intensity') {
      const ratio = this.config.stackMax > 1
        ? clamped / this.config.stackMax
        : 1;
      if (this.iconImg?.active) {
        this.iconImg.setAlpha(0.4 + 0.6 * ratio);
      }
    }
  }

  /** Update remaining duration — drives expiry feedback if showDuration is true. */
  setRemainingMs(ms: number): void {
    if (!this.config.showDuration || !this.playing) return;

    if (this.config.type === 'aura' && this.auraDelegate) {
      this.auraDelegate.setRemainingMs(ms);
    }
  }

  override destroy(): void {
    this.killBreatheTween();
    if (this.stackText) {
      this.stackText.destroy();
      this.stackText = null;
    }
    if (this.auraDelegate) {
      this.auraDelegate.destroy();
      this.auraDelegate = null;
    }
    super.destroy();
  }

  private startBreathe(): void {
    this.killBreatheTween();
    const scale = this.config.breatheScale ?? 0.1;
    const period = this.config.breathePeriodMs ?? 1500;
    if (!this.iconImg || scale <= 0) return;

    const baseScale = 1 / CRISP_SCALE;
    this.breatheTween = this.scene.tweens.add({
      targets: this.iconImg,
      scaleX: { from: baseScale, to: baseScale * (1 + scale) },
      scaleY: { from: baseScale, to: baseScale * (1 + scale) },
      duration: period / 2,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private killBreatheTween(): void {
    if (this.breatheTween) {
      this.breatheTween.destroy();
      this.breatheTween = null;
    }
  }
}
